import React from 'react'
import { useNavigate } from 'react-router-dom'

import {
  F0TableOfContent,
  OneDataCollection,
  TOCItem,
} from '@factorialco/f0-react/dist/experimental'
import { Calendar, Marketplace, Money, Placeholder } from '@factorialco/f0-react/icons/app'
import {
  DistributorProductVariantsDocument,
  DistributorProductVariantsQuery,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { useItManagementFeatureAvailability } from 'modules/it_management/hooks/useItManagementFeatureAvailability'
import { formatPeriod, formatPrice } from 'modules/it_management/lib/formatUtils'
import { useProductCatalog } from 'modules/it_management/lib/graphqlProductCatalogContext'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import emptyStateImage from './Empty-state-device-catalog-card.png'
import { TwoColumnLayout } from '@factorialco/f0-react'

type DistributorProductVariant =
  DistributorProductVariantsQuery['itManagement']['distributorProductVariantsConnection']['nodes'][number]

const DeviceCatalogPage: React.FC = () => {
  const navigate = useNavigate()
  const { queryContext, buildProductImageUrl, loadingProductCatalogContext } = useProductCatalog()
  const [selectedTocItems, setSelectedTocItems] = React.useState<Record<string, string>>({
    general: 'all',
  })
  const { company, access } = useCurrentSession()
  const {
    isItManagementEnabled,
    loading: featureLoading,
    error: featureError,
  } = useItManagementFeatureAvailability()

  React.useEffect(() => {
    track(trackingEvents.itManagement.deviceCatalogEvents.goToDeviceCatalog, {
      coming_from: 'side_bar',
      company_id: company.id,
      user_id: access.id,
    })
  }, [company.id, access.id])

  const goToItemDetail = (item: DistributorProductVariant) => {
    track(trackingEvents.itManagement.deviceCatalogEvents.goToDeviceDetail, {
      device_id: item.id,
      device_name: item.product?.name || item.sku || item.id,
      coming_from: 'catalog',
      company_id: company.id,
      user_id: access.id,
    })
    navigate(`/device-catalog/${item.id}`)
  }

  const source = useGraphqlDataCollectionSource(
    {
      document: DistributorProductVariantsDocument,
      fetchPolicy: 'network-only',
      perPage: 36,
      queryContext,
      search: {
        enabled: true,
      },
      mapData: (data: DistributorProductVariantsQuery) => {
        return data?.itManagement.distributorProductVariantsConnection?.nodes || []
      },
      mapPagination: (data: DistributorProductVariantsQuery) => {
        return {
          total: data?.itManagement.distributorProductVariantsConnection?.totalCount || 0,
        }
      },
      mapVariables: ({ search }) => {
        return {
          search: search,
        }
      },
      itemOnClick: (item: DistributorProductVariant) => () => {
        goToItemDetail(item)
      },
      itemActions: (item: DistributorProductVariant) => [
        {
          label: i18n.t('it_management.device_catalog.actions.view_options'),
          type: 'secondary' as const,
          icon: Marketplace,
          onClick: (_?: React.MouseEvent) => {
            goToItemDetail(item)
          },
        },
      ],
    },
    [queryContext, selectedTocItems]
  )

  const tableofContentItems: { id: string; title: string; items: TOCItem[] }[] = [
    {
      id: 'general',
      title: '',
      items: [
        {
          icon: Placeholder,
          id: 'all',
          label: 'All devices',
          onClick: (id) => setSelectedTocItems({ ...selectedTocItems, general: id }),
        },
        {
          icon: Placeholder,
          id: 'saved',
          label: 'Saved',
          onClick: (id) => setSelectedTocItems({ ...selectedTocItems, general: id }),
        },
        {
          icon: Placeholder,
          id: 'offers',
          label: 'Offers',
          onClick: (id) => setSelectedTocItems({ ...selectedTocItems, general: id }),
        },
      ],
    },
    {
      id: 'capabilities',
      title: 'Capabilities',
      items: [
        {
          icon: Placeholder,
          id: 'mdm-compatible',
          label: 'MDM Compatible',
          onClick: (id) => setSelectedTocItems({ ...selectedTocItems, capabilities: id }),
        },
      ],
    },
    {
      id: 'categories',
      title: 'Categories',
      items: [
        {
          icon: Placeholder,
          id: 'accessories',
          label: 'Accessories',
          onClick: (id) => setSelectedTocItems({ ...selectedTocItems, categories: id }),
        },
        {
          icon: Placeholder,
          id: 'audio-video',
          label: 'Audio & video',
          onClick: (id) => setSelectedTocItems({ ...selectedTocItems, categories: id }),
        },
        {
          icon: Placeholder,
          id: 'computers',
          label: 'Computers',
          onClick: (id) => setSelectedTocItems({ ...selectedTocItems, categories: id }),
        },
        {
          icon: Placeholder,
          id: 'phones',
          label: 'Mobile phones',
          onClick: (id) => setSelectedTocItems({ ...selectedTocItems, categories: id }),
        },
        {
          icon: Placeholder,
          id: 'networking',
          label: 'Networking & IT',
          onClick: (id) => setSelectedTocItems({ ...selectedTocItems, categories: id }),
        },
        {
          icon: Placeholder,
          id: 'tablets',
          label: 'Tablets',
          onClick: (id) => setSelectedTocItems({ ...selectedTocItems, categories: id }),
        },
        {
          icon: Placeholder,
          id: 'wearables',
          label: 'Wearables',
          onClick: (id) => setSelectedTocItems({ ...selectedTocItems, categories: id }),
        },
      ],
    },
  ]

  return (
    <ResponsivePage.Body
      variant="fullscreen"
      authorized={isItManagementEnabled}
      error={featureError}
      loading={featureLoading || loadingProductCatalogContext}
      height="full"
    >
      <TwoColumnLayout
        sticky
        sideContent={tableofContentItems.map((group) => (
          <F0TableOfContent
            key={group.title}
            title={group.title}
            items={group.items}
            activeItem={selectedTocItems[group.id]}
            hideChildrenCounter
          />
        ))}
        mainColumnPosition="right"
      >
        <OneDataCollection
          source={source}
          emptyStates={{
            'no-data': {
              title: i18n.t('it_management.device_catalog.empty_state.title'),
              description: i18n.t('it_management.device_catalog.empty_state.description'),
            },
          }}
          visualizations={[
            {
              type: 'card',
              options: {
                title: (item: DistributorProductVariant) => {
                  return item.product?.name ? item.product.name : item.sku || item.id
                },
                image: (item: DistributorProductVariant) => {
                  return buildProductImageUrl(item.firstProductMediaImageUrl, emptyStateImage)
                },
                imageFit: 'contain',
                imageSize: 'lg',
                blurredBackground: false,
                cardProperties: [
                  {
                    label: i18n.t('it_management.device_catalog.price_title'),
                    tooltip: i18n.t('it_management.device_catalog.price_title'),
                    icon: Money,
                    render: (item: DistributorProductVariant) => {
                      const price = formatPrice(item.firstOfferCost, item.firstOfferPeriod)
                      if (price === '-') return undefined
                      return {
                        type: 'text',
                        value: price,
                      }
                    },
                  },
                  {
                    label: i18n.t('it_management.device_catalog.period_title'),
                    tooltip: i18n.t('it_management.device_catalog.period_title'),
                    icon: Calendar,
                    render: (item: DistributorProductVariant) => {
                      const period = formatPeriod(item.firstOfferPeriod)
                      if (!period) return undefined

                      return {
                        type: 'text',
                        value: period,
                      }
                    },
                  },
                ],
              },
            },
          ]}
        />
      </TwoColumnLayout>
    </ResponsivePage.Body>
  )
}

export default DeviceCatalogPage
